import React, { useState } from 'react'
import {
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
  Alert,
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import ContentCopy from '@mui/icons-material/ContentCopy'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Schedule from '@mui/icons-material/Schedule'
import Payment from '@mui/icons-material/Payment'
import { default as LinkIcon } from '@mui/icons-material/Link'
import OpenInNew from '@mui/icons-material/OpenInNew'
import Warning from '@mui/icons-material/Warning'
import AccessTime from '@mui/icons-material/AccessTime'
import CurrencyRupee from '@mui/icons-material/CurrencyRupee'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getPriceStringWithCurrency } from 'src/mentor/helpers/utilityFunctions'
import { toast } from 'react-toastify'
import { styled } from '@mui/material/styles'
dayjs.extend(utc)

const PaymentLinkCard = styled(Card)(({ theme, isExpired, isPaid }) => ({
  borderRadius: '12px',
  border: isExpired
    ? '1px solid rgba(220, 38, 38, 0.2)'
    : isPaid
    ? '1px solid rgba(16, 185, 129, 0.3)'
    : '1px solid rgba(67, 56, 202, 0.2)',
  background: isExpired
    ? 'linear-gradient(135deg, rgba(220, 38, 38, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%)'
    : isPaid
    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%)'
    : 'linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.2s ease',
  opacity: isExpired ? 0.7 : 1,
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}))

const LinkDisplayBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  background: '#fff',
  borderRadius: '8px',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(67, 56, 202, 0.04)',
    borderColor: 'rgba(67, 56, 202, 0.2)',
  },
}))

const CopyButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  borderRadius: '8px',
  background: 'rgba(67, 56, 202, 0.1)',
  color: '#4338ca',
  '&:hover': {
    background: 'rgba(67, 56, 202, 0.2)',
  },
}))

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '8px',
  '&:last-child': {
    marginBottom: 0,
  },
}))

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.6)',
  minWidth: '100px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}))

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(0, 0, 0, 0.87)',
  flex: 1,
}))

export default function PaymentLinksViewer({ bookedSlot, closeDialog, refreshBookedSlots }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const allPaymentLinks = bookedSlot?.paymentLinks || []
  const [copiedLinkId, setCopiedLinkId] = useState(null)

  const getStatusChip = (status) => {
    const chipMap = {
      PENDING: {
        label: 'Pending',
        color: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.15)',
        borderColor: 'rgba(245, 158, 11, 0.3)',
        icon: <Schedule sx={{ fontSize: 14 }} />,
      },
      EXPIRED: {
        label: 'Expired',
        color: '#dc2626',
        bgColor: 'rgba(220, 38, 38, 0.15)',
        borderColor: 'rgba(220, 38, 38, 0.3)',
        icon: <Warning sx={{ fontSize: 14 }} />,
      },
      PAID: {
        label: 'Paid',
        color: '#10b981',
        bgColor: 'rgba(16, 185, 129, 0.15)',
        borderColor: 'rgba(16, 185, 129, 0.3)',
        icon: <CheckCircle sx={{ fontSize: 14 }} />,
      },
    }
    const chip = chipMap[status] || {
      label: status,
      color: '#6b7280',
      bgColor: 'rgba(107, 114, 128, 0.15)',
      borderColor: 'rgba(107, 114, 128, 0.3)',
      icon: null,
    }
    return chip
  }

  const handleCopyLink = (link, linkId) => {
    if (link) {
      navigator.clipboard.writeText(link)
      setCopiedLinkId(linkId)
      toast.success('Payment link copied to clipboard')
      setTimeout(() => setCopiedLinkId(null), 2000)
    }
  }

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A'
    return dayjs.utc(dateTime).local().format('DD MMM, YYYY hh:mm A')
  }

  const isLinkExpired = (paymentLink) => {
    if (!paymentLink?.paymentLinkExpiredTime) return false
    return dayjs.utc(paymentLink.paymentLinkExpiredTime).isBefore(dayjs())
  }

  const patientName = bookedSlot?.userName || bookedSlot?.patientInfo?.name || 'Patient'
  const eventTime = bookedSlot?.startTime
    ? dayjs.utc(bookedSlot.startTime).local().format('DD MMM, YYYY hh:mm A')
    : ''

  return (
    <>
      <DialogTitle
        sx={{
          padding: '24px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#4338ca', marginBottom: '4px' }}>
              Payment Links
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', marginTop: '8px' }}>
              <Chip
                icon={<Payment sx={{ fontSize: 16 }} />}
                label={patientName}
                size="small"
                sx={{
                  background: 'rgba(67, 56, 202, 0.1)',
                  color: '#4338ca',
                  fontWeight: 500,
                }}
              />
              {eventTime && (
                <Chip
                  icon={<AccessTime sx={{ fontSize: 16 }} />}
                  label={eventTime}
                  size="small"
                  sx={{
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: '#6366f1',
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
          </Box>
          <IconButton
            onClick={closeDialog}
            sx={{
              color: 'rgba(0, 0, 0, 0.6)',
              padding: isMobile ? '12px' : '8px',
              minWidth: isMobile ? '48px' : '40px',
              minHeight: isMobile ? '48px' : '40px',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px' }}>
        {allPaymentLinks.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: '12px' }}>
            No payment links available for this booking.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {allPaymentLinks.map((paymentLink, index) => {
              const statusChip = getStatusChip(paymentLink.paymentStatus)
              const expired = isLinkExpired(paymentLink)
              const isPaid = paymentLink.paymentStatus === 'PAID'

              return (
                <PaymentLinkCard key={paymentLink.id || index} isExpired={expired} isPaid={isPaid}>
                  <CardContent sx={{ padding: '20px !important' }}>
                    {/* Header with Status and Amount */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '16px',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          icon={statusChip.icon}
                          label={statusChip.label}
                          size="small"
                          sx={{
                            height: '24px',
                            fontSize: '11px',
                            fontWeight: 700,
                            background: statusChip.bgColor,
                            color: statusChip.color,
                            border: `1px solid ${statusChip.borderColor}`,
                          }}
                        />
                        {expired && (
                          <Chip
                            label="Expired"
                            size="small"
                            sx={{
                              height: '22px',
                              fontSize: '10px',
                              background: 'rgba(220, 38, 38, 0.1)',
                              color: '#dc2626',
                              border: '1px solid rgba(220, 38, 38, 0.2)',
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '18px',
                          fontWeight: 700,
                          color: '#4338ca',
                        }}
                      >
                        <CurrencyRupee sx={{ fontSize: '20px' }} />
                        {getPriceStringWithCurrency(paymentLink.amount, paymentLink?.currency, true)}
                      </Box>
                    </Box>

                    <Divider sx={{ marginBottom: '16px' }} />

                    {/* Payment Link Details */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {paymentLink?.description && (
                        <InfoRow>
                          <InfoLabel>Description</InfoLabel>
                          <InfoValue>{paymentLink.description}</InfoValue>
                        </InfoRow>
                      )}

                      {paymentLink?.platform && (
                        <InfoRow>
                          <InfoLabel>Platform</InfoLabel>
                          <InfoValue>{paymentLink.platform}</InfoValue>
                        </InfoRow>
                      )}

                      {paymentLink?.paymentLinkExpiredTime && (
                        <InfoRow>
                          <InfoLabel>
                            <Schedule sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            Expiry Time
                          </InfoLabel>
                          <InfoValue>{formatDateTime(paymentLink.paymentLinkExpiredTime)}</InfoValue>
                        </InfoRow>
                      )}

                      {paymentLink?.createdTime && (
                        <InfoRow>
                          <InfoLabel>
                            <AccessTime sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            Created At
                          </InfoLabel>
                          <InfoValue>{formatDateTime(paymentLink.createdTime)}</InfoValue>
                        </InfoRow>
                      )}

                      {/* Payment Link */}
                      {paymentLink?.paymentLink && (
                        <Box sx={{ marginTop: '8px' }}>
                          <InfoLabel sx={{ marginBottom: '8px' }}>
                            <LinkIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                            Payment Link
                          </InfoLabel>
                          <LinkDisplayBox>
                            <Box
                              sx={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '13px',
                                color: '#4338ca',
                                textDecoration: 'none',
                              }}
                              component="a"
                              href={paymentLink.paymentLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {paymentLink.paymentLink}
                            </Box>
                            <Tooltip title={copiedLinkId === paymentLink.id ? 'Copied!' : 'Copy Link'}>
                              <CopyButton
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleCopyLink(paymentLink.paymentLink, paymentLink.id)
                                }}
                                size="small"
                              >
                                {copiedLinkId === paymentLink.id ? (
                                  <CheckCircle sx={{ fontSize: 18, color: '#10b981' }} />
                                ) : (
                                  <ContentCopy sx={{ fontSize: 18 }} />
                                )}
                              </CopyButton>
                            </Tooltip>
                            <Tooltip title="Open in new tab">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  window.open(paymentLink.paymentLink, '_blank', 'noopener,noreferrer')
                                }}
                                sx={{
                                  padding: '8px',
                                  borderRadius: '8px',
                                  background: 'rgba(67, 56, 202, 0.1)',
                                  color: '#4338ca',
                                  '&:hover': {
                                    background: 'rgba(67, 56, 202, 0.2)',
                                  },
                                }}
                              >
                                <OpenInNew sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </LinkDisplayBox>
                        </Box>
                      )}

                      {!paymentLink?.paymentLink && (
                        <Alert severity="warning" sx={{ borderRadius: '8px', marginTop: '8px' }}>
                          Payment link not available
                        </Alert>
                      )}
                    </Box>
                  </CardContent>
                </PaymentLinkCard>
              )
            })}
          </Box>
        )}
      </DialogContent>
    </>
  )
}
